import {
  faCog,
  faEllipsisVertical,
  faFingerprint,
  faTrashCanCheck,
} from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { orderBy } from 'lodash'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { useNavigate, useSearchParams } from 'react-router-dom'
import IWContextMenu from 'shared/components/thunderbolt/IWContextMenu'
import IWContextMenuItem from 'shared/components/thunderbolt/IWContextMenuItem'
import IWContextDivider from 'shared/components/thunderbolt/IWContextMenuItemDivider'
import IWGeneralBadge from 'shared/components/thunderbolt/IWGeneralBadge'
import IWGenericTable from 'shared/components/thunderbolt/IWGenericTable'
import {
  deleteLocationAttribute,
  getLsAdminLocationAttributes,
  LocationAttribute,
} from 'shared/loadSchedulingClient'
import styled from 'styled-components'
import DeleteLocationAttributeModal from './DeleteLocationAttributeModal'
import LsAdminPageSectionHeader from './LsAdminPageSectionHeader'
import { lsAdminLocationAttributesTableSchema } from './tableSchemas'

const StyledBadgeDiv = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`

type TableItem = {
  locationAttributeId: string
  credentialId: string
  configId: string
  market: string
  brand: string
  zone: string
  utility: string
  location: string
  locationName: string
}

const tableColumns = lsAdminLocationAttributesTableSchema

const USE_QUERY_KEY = 'ls-admin-location-attributes'

export default function LsAdminLocationAttributes() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [items, setItems] = useState<TableItem[]>([])
  const [currentViewableCredentialId, setCurrentViewableCredentialId] =
    useState<string | null>(null)
  const [currentViewableConfigId, setCurrentViewableConfigId] = useState<
    string | null
  >(null)
  const [locationIdToDelete, setLocationConfigIdToDelete] = useState<
    string | undefined
  >(undefined)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)

  const { data, isLoading } = useQuery({
    queryKey: [USE_QUERY_KEY, currentViewableCredentialId],
    queryFn: async () => getLsAdminLocationAttributes(),
  })

  const deleteMutation = useMutation({
    mutationKey: [USE_QUERY_KEY, 'create'],
    mutationFn: (locationAttributeId: string) =>
      deleteLocationAttribute(locationAttributeId),
    onSuccess: () => queryClient.invalidateQueries([USE_QUERY_KEY]),
  })

  const mapper = (locationAttributes: LocationAttribute[]) => {
    let filtered = locationAttributes || []

    if (currentViewableConfigId) {
      filtered = filtered.filter(
        (la) => la.configId === currentViewableConfigId,
      )
    }

    if (currentViewableCredentialId) {
      filtered = filtered.filter(
        (la) => la.credentialId === currentViewableCredentialId,
      )
    }

    return orderBy(filtered, [
      'market',
      'brand',
      'zone',
      'utility',
      'locationName',
      'location',
    ]).map((item) => ({
      ...item,
      isConfigActive: (
        <StyledBadgeDiv>
          <IWGeneralBadge
            label={t(`common.${item?.isConfigActive ? 'active' : 'disable'}`)}
            color={item?.isConfigActive ? 'primary' : 'alert'}
          />
        </StyledBadgeDiv>
      ),
      status: (
        <StyledBadgeDiv>
          <IWGeneralBadge
            label={t(
              `common.${item.isCredentialActive ? 'active' : 'disable'}`,
            )}
            color={item.isCredentialActive ? 'primary' : 'alert'}
          />
        </StyledBadgeDiv>
      ),

      contextMenu: (
        <IWContextMenu
          positions={['bottom', 'top']}
          menu={[
            <IWContextMenuItem
              as="routerLink"
              key={`ls-la-view-configs-${item.credentialId}`}
              label={t('loadScheduling.contextMenus.viewConfigs')}
              icon={faCog}
              to={`/admin/load-scheduling/configs?configId=${item.configId}`}
            />,

            <IWContextMenuItem
              as="routerLink"
              key={`ls-la-view-creds-${item.configId}`}
              data-testid={`ls-la-view-creds-${item.configId}`}
              label={t('loadScheduling.contextMenus.viewCredentials')}
              icon={faFingerprint}
              to={`/admin/load-scheduling/credentials?configId=${item.configId}`}
            />,

            <IWContextDivider />,

            <IWContextMenuItem
              as="button"
              key={`ls-la-delete-${item.configId}`}
              data-testid={`ls-la-delete-${item.configId}`}
              label={t('button.delete')}
              icon={faTrashCanCheck}
              onClick={() => {
                setLocationConfigIdToDelete(item.locationAttributeId)
                setIsDeleteModalOpen(true)
              }}
            />,
          ]}
        >
          <FontAwesomeIcon icon={faEllipsisVertical} />
        </IWContextMenu>
      ),
    }))
  }

  useEffect(() => {
    const queryParamsCredentialId = searchParams.get('credentialId')
    const queryParamsConfigId = searchParams.get('configId')

    setCurrentViewableCredentialId(queryParamsCredentialId || '')
    setCurrentViewableConfigId(queryParamsConfigId || '')
  }, [])

  useEffect(() => {
    if (data?.length) {
      setItems(mapper(data))
    }
  }, [data])

  return (
    <main>
      <LsAdminPageSectionHeader
        header={t('loadScheduling.titles.adminLocationAttribute')}
        desc={t('loadScheduling.descriptions.adminLocationAttribute')}
        buttonLabel={t('loadScheduling.labels.locationAttribute')}
        onButtonClick={() => {
          navigate('/admin/load-scheduling/location-attributes/create')
        }}
      >
        {Boolean(items?.length) && !isLoading && (
          <IWGenericTable
            border
            isLoading={isLoading}
            items={items}
            columns={tableColumns}
            showColumnSettings
          />
        )}
      </LsAdminPageSectionHeader>

      <DeleteLocationAttributeModal
        isModalOpen={isDeleteModalOpen}
        locationAttributeId={locationIdToDelete}
        onCancel={() => setIsDeleteModalOpen(false)}
        onDelete={async (locationAttributeId?: string) => {
          if (!locationAttributeId) {
            return
          }

          await deleteMutation.mutateAsync(locationAttributeId)
          setIsDeleteModalOpen(false)
        }}
      />
    </main>
  )
}
